import { Inject } from '@odinjs/odin';

import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { ActionAPI, BaseModalProps, DeckAPI, ModalButton, ModalProps, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';

import { NursingInterventionsDetails } from '../nursing-interventions-types';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from '../../../services/timeline-service';
import BaseController from '../../../../BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import { Endpoint } from '../../../../../constants/endpoints';
import { FragmentNames, NursingInterventionsViewerAliases } from '../../../../../codegen';
import { MedicationStatus } from '../../administration-schedule/administration-schedule-types';
import { AbortableCloseEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ApplicationRoute } from '../../../../../constants/routes';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { YesNoType } from '../../../../../utils/enum-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../../utils/parameters';
import { MedicationAndDateHeaderParams } from '../../../../commons/header/medication-and-date-header-params';
import { MedicationViewerParams } from '../../medication-viewer/medication-viewer-params';
import { ModalButtonType, ModalType } from '@philips-internal/oswald-ui-constants';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { ReservedBloodProductsStatus } from '@philips-emr/health-professional-components/src/components/blood-management-details-card/blood-management-details-card-types';

@Controller({ component: NursingInterventionsViewerAliases.NURSING_INTERVENTIONS_VIEWER_LAYOUT, fragment: FragmentNames.NURSING_INTERVENTIONS_VIEWER })
export default class NursingInterventionsViewController extends BaseController {

  @Inject(NursingInterventionsViewerAliases.PANEL_NURSING_INTERVENTIONS_CONTENT)
  private contentPanelApi!: PanelAPI;

  @OnMount(NursingInterventionsViewerAliases.NURSING_INTERVENTIONS_VIEWER_LAYOUT)
  handleMountNursingInterventionsLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    this.contentPanelApi.setVisible(false)
    const data = MedicineAndSolutionsParams?.params?.startId ? MedicineAndSolutionsParams.params.startId : '';
    this.fetchNursingInterventions().then((response) => {
      const nursingInterventionsDetails = response as NursingInterventionsDetails;
      const timelineItemDetail = TimelineContext.get() as TimeLineEventMedication;

      MedicineAndSolutionsParams.params = {
        ...MedicineAndSolutionsParams.params,
        cdIntervalId: nursingInterventionsDetails?.interval
      };

      MedicationViewerParams.params = {
        eventDate: data,
        hourSequence: MedicineAndSolutionsParams.params.hourSequence,
        itemCode: MedicineAndSolutionsParams.params.cdMaterialId,
        medicationName: MedicineAndSolutionsParams.params.dsMaterialId,
        prescriptionNumber: MedicineAndSolutionsParams.params.nrPrescriptionId,
      }

      MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
        medicationName: timelineItemDetail.description,
        scheduleDate: timelineItemDetail.start,
        type: 'NURSING_INTERVENTIONS'
      };

      this.context.set({ ...nursingInterventionsDetails, encounterId, patientId});
    }).finally(() => {
      this.contentPanelApi.setVisible(true)
    })
  }

  @OnMount(NursingInterventionsViewerAliases.NURSING_INTERVENTIONS_DECK)
  handleMountNursingInterventionsDeck({ target }: EventData<DeckAPI>): void {
    const timelineItemDetail = TimelineContext.get() as TimeLineEventMedication;
    const nursingInterventionsDetails = this.context.getBulkData() as NursingInterventionsDetails
    target.setData([{
      description:  timelineItemDetail.description,
      group: nursingInterventionsDetails.interventionGroup,
      scheduleDate:  timelineItemDetail.start,
      side: nursingInterventionsDetails.side,
      statusItem:  timelineItemDetail.status,
      topography: nursingInterventionsDetails.topography
    }]);
  }

  @OnMount(NursingInterventionsViewerAliases.EXECUTE_TIME_PANEL)
  handleMountExecuteButton({ target }: EventData<PanelAPI>): void {
    target.setVisible(this.shouldStart());
  }

  private shouldStart(): boolean {
    const timelineItemDetail = TimelineContext.get() as TimeLineEventMedication;
    const pendingStatus = 'PENDING'
    const allowStartNursingInterventionItem = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOWS_TO_PERFORM_PRESCRIPTION_ITEMS);
    return allowStartNursingInterventionItem !== YesNoType.NO.valueOf() && timelineItemDetail.status === pendingStatus;
  }

  @OnClick(NursingInterventionsViewerAliases.EXECUTE_TIME_BUTTON)
  handleClickExecuteButton(): void {
    const startExpression = MessageService.translate('txt.start-nursing-interventions');
    const nursingInterventions = this.context.getBulkData();
    const startNursingInterventionsFragment = FragmentNames.NURSING_INTERVENTIONS_START
    const dialogModal: BaseModalProps = {
      contextData: { nursingInterventions: nursingInterventions },
      focusOnOpen: false,
      fragment: startNursingInterventionsFragment,
      id: startNursingInterventionsFragment,
      onBeforeClose: (event) => this.openDialogModalConfirm(event, startNursingInterventionsFragment),
      title: startExpression,
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);
  }

  @OnMount(NursingInterventionsViewerAliases.RESCHEDULE_NURSING_INTERVENTIONS_ACTION)
  handleRescheduleNursingMount(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    actionApi.setEnabled(this.isAllowedToReschedule());
  }

  @OnClick(NursingInterventionsViewerAliases.RESCHEDULE_NURSING_INTERVENTIONS_ACTION)
  handleRescheduleNursingClick(): void {
    const { patientId, encounterId } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(ApplicationRoute.ADEP_NURSING_INTERVENTIONS_RESCHEDULE, { encounterId, patientId });
  }

  private isAllowedToReschedule(): boolean {
    const param29 = FoundationUtils.findModuleParam('ADEP#1113', ADEP_E1113Parameters.ALLOW_RESCHEDULE_MEDICATION);
    const { status } = TimelineContext.get();
    return YesNoType.YES === param29 && [ReservedBloodProductsStatus.PENDING.toString(), MedicationStatus.PENDING.toString()].includes(status);
  }

  private openDialogModalConfirm(event: AbortableCloseEvent, fragment: FragmentNames): void {
    event.abort();

    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { },
      title: 'tws.biometry.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => ModalService.close(fragment),
      title: 'tws.file-uploader-modal.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: ModalProps = {
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: MessageService.translate('text.reset items-want-to-continue'),
      dockButtons: buttons,
      focusOnOpen: true,
      title: 'text.message-alert-attention',
      uuid: generateUUID(),
    };

    ModalService.openDialog(dialogModal);
  }

  @OnMount(NursingInterventionsViewerAliases.SUSPEND_NURSING_INTERVENTIONS_ACTION)
  handleMountSuspendNursingInterventionsAction(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    actionApi.setEnabled(this.shouldShowSuspend())
  }

  @OnClick(NursingInterventionsViewerAliases.SUSPEND_NURSING_INTERVENTIONS_ACTION)
  handleClickSuspendNursingInterventionsAction(): void {
    const { patientId, encounterId } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(ApplicationRoute.ADEP_SUSPEND_ITEM, {
      encounterId,
      patientId,
    });
  }

  private shouldShowSuspend(): boolean {
    const timelineItemDetail = TimelineContext.get() as TimeLineEventMedication;
    const pendingStatus = 'PENDING'
    const allowSuspendNursingInterventionItem = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_TO_SUSPEND_SNCP_ITEMS);
    return allowSuspendNursingInterventionItem !== YesNoType.NO.valueOf() && timelineItemDetail.status === pendingStatus;
  }

  @OnMount(NursingInterventionsViewerAliases.REVERSE_NURSING_INTERVENTATIONS_ACTION)
  handleMountActionReverse(event: MountEvent): void {
    const actionApi = event.target as ActionAPI;
    actionApi.setEnabled(this.isAllowedToReverse());
  }

  @OnClick(NursingInterventionsViewerAliases.REVERSE_NURSING_INTERVENTATIONS_ACTION)
  handleClickActionReverse(): void {
    const { itemSequence, prescriptionNumber } = TimelineContext.get() as TimeLineEventMedication;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const itemType = PrescriptionType.NURSING_INTERVENTIONS;
    applicationContext.redirect(ApplicationRoute.ADEP_NURSING_INTERVENTIONS_REVERSE, { encounterId, itemType, patientId, prescriptionNumber, sequenceNumber: itemSequence });
  }

  private fetchNursingInterventions(): Promise<NursingInterventionsDetails> {
    const { itemCode, prescriptionNumber } = TimelineContext.get() as TimeLineEventMedication;

    return HttpService.get<NursingInterventionsDetails>(Endpoint.EMAR_GET_NURSING_INTERVENTIONS_INFORMATION, {
      prescriptionSequence: prescriptionNumber,
      procedureSequence: itemCode,
    }).then((response) => {
      const responseBody = response.body as NursingInterventionsDetails;
      return responseBody;
    })
  }

  private isAllowedToReverse(): boolean {
    const allowRevertParam = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_REVERSING_TIME_MANAGEMENT)
    if (allowRevertParam !== YesNoType.YES) {
      return false
    }
    const { hourStatus } = this.historyService.getCurrentRouteParams();
    return hourStatus !== MedicationStatus.PENDING.valueOf();
  }
}